import React, { type ComponentRef } from 'react'

import * as HoverCard from '@radix-ui/react-hover-card'

import { AutomationSpecifications } from '@/components/atoms/AutomationSpecifications/AutomationSpecifications.tsx'
import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { cn } from '@/lib/utils.ts'

export interface AutomationSpecificationsPopupProps extends React.ComponentPropsWithoutRef<typeof HoverCard.Root> {
  mode: 'manual' | 'solver'
  items: string[]
  levelName: string
  title: string
  description: string
}

const itemLevel = (mode: 'manual' | 'solver') => {
  switch (mode) {
    case 'solver':
      return 'best'
    default:
      return 'manual'
  }
}

const titleColor = (mode: 'manual' | 'solver') => {
  switch (mode) {
    case 'solver':
      return 'text-green-500' as const
    default:
      return 'text-orange-400' as const
  }
}

const AutomationIcon: React.FC<{ mode: 'manual' | 'solver' }> = ({ mode }) => {
  switch (mode) {
    case 'solver':
      return <Icons.shootingStar className="w-4 fill-green-500" />
    default:
      return <Icons.manualAssignation className="w-4 fill-orange-400" />
  }
}

export const AutomationSpecificationsPopup = React.forwardRef<
  ComponentRef<typeof HoverCard.Trigger>,
  AutomationSpecificationsPopupProps
>(({ items, levelName, title, description, mode, children, ...props }, ref) => (
  <HoverCard.Root openDelay={0} closeDelay={0} {...props}>
    <HoverCard.Trigger ref={ref} asChild>
      {children}
    </HoverCard.Trigger>
    <HoverCard.Portal>
      <HoverCard.Content align="start">
        <AutomationSpecifications
          className="mt-2"
          items={items?.map((name) => ({ name, level: itemLevel(mode), levelName }))}>
          <div className="flex flex-col gap-2 select-none">
            <div className="flex items-baseline gap-2">
              <AutomationIcon mode={mode} />
              <p className={cn('text-lg font-bold', titleColor(mode))}>{title}</p>
            </div>
            <p className="text-base text-black">{description}</p>
          </div>
        </AutomationSpecifications>
      </HoverCard.Content>
    </HoverCard.Portal>
  </HoverCard.Root>
))
